import { Flex } from "@chakra-ui/react";
import { useIntl } from "react-intl";

import { RanaLoading, RanaModal } from "@nens/ranalib";
import { RanaForm, SubmitFunction } from "@nens/ranalib";
import { capitalizeFirst } from "@nens/shared-utils";

import { cancelButtonText } from "@/translations";
import { ProjectFileOrDirectory } from "@/types/api-model-simple-names";
import { getDuplicateFileId } from "@/utils/get-duplicate-file-id";
import { getParentPath } from "@/utils/get-file-parent-path";
import { getNameFromFileId } from "@/utils/get-name-from-file-id";

import { useDuplicateProjectFileOrDirectory } from "../api/duplicate";
import { validateFileName } from "../utils/validate-file-name";

interface ProjectUserRenameModalProps {
  existingFileIds: string[];
  directoryOrFile: ProjectFileOrDirectory;
  onClose: () => void;
}

type FileDuplicateFormValues = {
  name: string;
};

export function ProjectFileOrDirectoryDuplicateModal({
  existingFileIds,
  directoryOrFile,
  onClose,
}: ProjectUserRenameModalProps) {
  const intl = useIntl();
  const title = intl.formatMessage({ id: "projectfile.duplicate", defaultMessage: "Duplicate" });
  const { isMutating, mutate } = useDuplicateProjectFileOrDirectory();

  const { id, type } = directoryOrFile;

  const validate = (values: FileDuplicateFormValues) =>
    validateFileName(intl, type, existingFileIds, values);

  // Submit can assume validate returned no errors.
  const submit: SubmitFunction<FileDuplicateFormValues> = async (values, onError) => {
    if (values.name && typeof values.name == "string")
      mutate(
        {
          sourceId: id,
          destinationId: getParentPath(id) + values.name,
          type,
        },
        {
          onSuccess: () => {
            onClose();
          },
          onError,
        }
      );
  };

  return (
    <RanaForm
      initialValues={{ name: getNameFromFileId(getDuplicateFileId(id, existingFileIds)) }}
      validate={validate}
      doSubmit={submit}
    >
      <RanaModal
        isOpen
        onClose={onClose}
        topBar={title + " " + getNameFromFileId(id)}
        maxW="xl"
        buttons={[
          {
            children: cancelButtonText,
            variant: "tertiary",
            isDisabled: isMutating,
            onClick: onClose,
          },
          <RanaForm.SubmitButton key={title} isDisabled={isMutating}>
            {title}
          </RanaForm.SubmitButton>,
        ]}
      >
        <Flex flexDir="column" gap={2}>
          <RanaForm.Input
            submitOnEnter
            autoFocus
            field="name"
            label={capitalizeFirst(type) + " name"}
            isRequired
          />
          {isMutating && <RanaLoading size="md" isCentered />}
          <RanaForm.Errors />
        </Flex>
      </RanaModal>
    </RanaForm>
  );
}
